import type { VendorEvent } from './types'
import { TESLA_CATEGORY_LABELS } from './tesla'

export type DriveProblem = 'empty' | 'not-teslacam' | 'encrypted' | 'unreadable'

export const UNKNOWN_DAY_KEY = 'unknown'

export interface DayGroup {
  /** Local calendar day as YYYY-MM-DD, or UNKNOWN_DAY_KEY. */
  key: string
  date: Date | null
  events: VendorEvent[]
}

export type NavigatorRow =
  | { kind: 'day'; key: string; date: Date | null; count: number }
  | { kind: 'event'; key: string; event: VendorEvent }

const SAMPLE_SIZE = 5
const MP4_HEADER_BYTES = 12
const CLIP_NAME = /^\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2}-.*\.mp4$/

function localDayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function validDate(d: Date | undefined): Date | null {
  return d && !isNaN(d.getTime()) ? d : null
}

/** Groups events by local calendar day, newest day first, with an unknown-date group last. */
export function groupEventsByDay(events: VendorEvent[]): DayGroup[] {
  const byDay = new Map<string, DayGroup>()
  for (const event of events) {
    const date = validDate(event.dateTime)
    const key = date ? localDayKey(date) : UNKNOWN_DAY_KEY
    const day = date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null
    const group = byDay.get(key) ?? { key, date: day, events: [] }
    group.events.push(event)
    byDay.set(key, group)
  }

  const time = (e: VendorEvent) => validDate(e.dateTime)?.getTime() ?? 0
  const groups = [...byDay.values()]
  groups.forEach((g) => g.events.sort((a, b) => time(b) - time(a)))
  return groups.sort((a, b) => {
    if (!a.date) return 1
    if (!b.date) return -1
    return b.date.getTime() - a.date.getTime()
  })
}

/** Flattens day groups into a day-header row followed by its event rows, for a virtual list. */
export function toRows(groups: DayGroup[]): NavigatorRow[] {
  return groups.flatMap((g) => [
    { kind: 'day' as const, key: `day:${g.key}`, date: g.date, count: g.events.length },
    ...g.events.map((event) => ({ kind: 'event' as const, key: event.id, event }))
  ])
}

function relativePath(file: File): string {
  return (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? ''
}

function isTeslaFile(file: File): boolean {
  if (file.name === 'event.json' || CLIP_NAME.test(file.name)) return true
  return relativePath(file)
    .split('/')
    .some((seg) => seg in TESLA_CATEGORY_LABELS)
}

function readBytes(file: File, count: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(file.slice(0, count))
  })
}

function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })
}

// 'ftyp' at byte 4 marks an MP4 container.
function isMp4Header(bytes: Uint8Array): boolean {
  return bytes.length >= 8 && String.fromCharCode(...bytes.slice(4, 8)) === 'ftyp'
}

async function isReadableClip(file: File): Promise<boolean> {
  return isMp4Header(await readBytes(file, MP4_HEADER_BYTES))
}

async function isReadableEventJson(file: File): Promise<boolean> {
  const text = await readText(file)
  try {
    JSON.parse(text)
    return true
  } catch {
    return false
  }
}

/**
 * Checks a picked folder before parsing it. Samples a few clips and event.json files rather than reading
 * the drive: encrypted means nothing sampled is a valid MP4 or JSON. Heuristic, no decryption.
 */
export async function detectDriveProblem(files: File[]): Promise<DriveProblem | null> {
  if (files.length === 0) return 'empty'
  if (!files.some(isTeslaFile)) return 'not-teslacam'

  const clips = files.filter((f) => f.name.toLowerCase().endsWith('.mp4')).slice(0, SAMPLE_SIZE)
  const jsons = files.filter((f) => f.name === 'event.json').slice(0, SAMPLE_SIZE)
  if (clips.length === 0 && jsons.length === 0) return 'empty'

  try {
    const clipOk = await Promise.all(clips.map(isReadableClip))
    const jsonOk = await Promise.all(jsons.map(isReadableEventJson))
    return clipOk.some(Boolean) || jsonOk.some(Boolean) ? null : 'encrypted'
  } catch (err) {
    console.error('TeslaCam drive check failed', err)
    return 'unreadable'
  }
}
